import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ScrapedJob, ScrapedJobService } from 'src/app/services/scraped-job/scraped-job.service';

export interface ScrapeRun {
  id: number;
  fuentes: string;
  ejecutadoEn: string;
  totalVacantes: number;
  errorLog?: string;
  apifyActorsJson?: string;
  iniciadoPor?: string;
  duracionSegundos?: number;
  metricas?: { occ: number; ct: number; hl: number; apify: number; remotive: number; himalayan: number; workingNomads: number; wwr: number; occDup: number; ctDup: number; hlDup: number; apifyDup: number; remotiveDup: number; himalayanDup: number; workingNomadsDup: number; wwrDup: number; detOk: number; detFail: number; llmOk: number; llmFail: number; dup: number } | null;
  errores?: { occ?: string; ct?: string; hl?: string; apify?: string; remotive?: string; himalayan?: string; workingNomads?: string; wwr?: string } | null;
  apifyActors?: ApifyActorStat[];
}

export interface ApifyActorStat {
  id: string;
  label: string;
  count: number;
  dup: number;
  saved: number;
  error: string | null;
}

@Component({
  selector: 'app-admin-scraper',
  templateUrl: './admin-scraper.component.html',
})
export class AdminScraperComponent implements OnInit, OnDestroy {

  ejecutando = false;
  scraperEnEjecucion = false;
  deteniendo = false;
  mensajeEjecutar: string | null = null;

  progreso = {
    fuente: '', count: 0, total: 0, guardadas: 0,
    occCount: 0, ctCount: 0, hlCount: 0, apifyCount: 0, apifySaved: 0,
    remotiveCount: 0, himalayanCount: 0, workingNomadsCount: 0, wwrCount: 0,
    dupRun: 0, dupBD: 0,
    occDup: 0, ctDup: 0, hlDup: 0, apifyDup: 0,
    remotiveDup: 0, himalayanDup: 0, workingNomadsDup: 0, wwrDup: 0,
    detOk: 0, detFail: 0,
    llmOk: 0, llmFail: 0, llmError: '',
    errGuardado: 0,
    occError: null as string | null, ctError: null as string | null, hlError: null as string | null,
    apifyError: null as string | null,
    remotiveError: null as string | null, himalayanError: null as string | null,
    workingNomadsError: null as string | null, wwrError: null as string | null,
    iniciadoPor: null as string | null, iniciadoEn: null as string | null,
    dupSampleUrls: '' as string,
    apifyActors: [] as ApifyActorStat[]
  };
  private pollingTimer: any = null;
  ultimoResumen: typeof this.progreso | null = null;

  get porcentaje(): number {
    if (!this.scraperEnEjecucion || this.progreso.total === 0) return 0;
    return Math.min(99, Math.round((this.progreso.count / this.progreso.total) * 100));
  }

  stats: { total: number; publicadas: number; rechazadas: number; pendientes: number; [fuente: string]: number } = {
    total: 0, publicadas: 0, rechazadas: 0, pendientes: 0
  };

  get fuentesStats(): { fuente: string; count: number }[] {
    const excluir = new Set(['total', 'publicadas', 'rechazadas', 'pendientes']);
    return Object.entries(this.stats)
      .filter(([k]) => !excluir.has(k))
      .map(([fuente, count]) => ({ fuente, count }))
      .sort((a, b) => b.count - a.count);
  }

  config: any = null;
  iaSet = false;
  tabActiva: 'vacantes' | 'config' | 'historial' = 'historial';
  guardandoConfig = false;
  mensajeConfig: string | null = null;
  mensajeConfigError = false;

  configForm = {
    occMaxPages: 5, occEnabled: true,
    ctMaxPages: 5,  ctEnabled: true,
    hlMaxPages: 5,  hlEnabled: true,
    pauseMs: 20000,
    autoMinPauseMs: 20000,
    autoMaxPauseMs: 50000,
    cron: '0 0 3 * * MON-SAT',
    horaEjecucion: 3,
    horaEjecucion2: -1,
    horaEjecucion3: -1,
    iaModelo: 'deepseek-v4-flash',
    // iaToken eliminado — el token de IA se gestiona en /admin/config
    apifyActors: [] as any[],
    remotiveEnabled: true, remotiveMaxJobs: 100,
    himalayanEnabled: true, himalayanMaxJobs: 100,
    workingNomadsEnabled: true, workingNomadsMaxJobs: 100,
    wwrEnabled: true, wwrMaxJobs: 100,
    // Asignación de ejecución por fuente
    occRun: 'BOTH', ctRun: 'BOTH', hlRun: 'BOTH',
    remotiveRun: 'BOTH', himalayanRun: 'BOTH', workingNomadsRun: 'BOTH', wwrRun: 'BOTH',
    premiumProxies: ''
  };

  mostrarApifyToken: { [actorId: string]: boolean } = {};
  revealedApifyTokens: { [actorId: string]: string } = {};
  loadingTokens = false;

  apifyBalances: { [actorId: string]: any } = {};
  cargandoBalance: { [actorId: string]: boolean } = {};

  filtroFuenteRun: string = 'ALL';

  historial: ScrapeRun[] = [];
  cargandoHistorial = false;
  eliminandoRun: number | null = null;
  runExpandido: number | null = null;
  vacantesPorRun: { [runId: number]: any[] } = {};
  resultadosPorRun: { [runId: number]: any[] } = {};
  cargandoVacantes: number | null = null;
  rechazandoVacante = new Set<number>();

  constructor(private scraperService: ScrapedJobService, private router: Router, private authService: AuthService) {}

  ngOnInit(): void {
    this.cargarStats();
    this.cargarConfig();
    this.cargarHistorial();
    this.verificarEstadoScraper();
  }

  ngOnDestroy(): void {
    this.detenerPolling();
  }

  cargarStats(): void {
    this.scraperService.stats().subscribe({
      next: (res) => { this.stats = res?.data ?? res; }
    });
  }

  cargarConfig(): void {
    this.scraperService.getConfig().subscribe({
      next: (res) => {
        this.config = res?.data ?? res;
        this.revealedApifyTokens = {};
        this.mostrarApifyToken = {};
        this.allApifyTokensLoaded = false;
        if (this.config) {
          this.configForm.occMaxPages  = this.config.occ?.maxPages  ?? 5;
          this.configForm.occEnabled   = this.config.occ?.enabled   ?? true;
          this.configForm.ctMaxPages   = this.config.computrabajo?.maxPages ?? 5;
          this.configForm.ctEnabled    = this.config.computrabajo?.enabled  ?? true;
          this.configForm.hlMaxPages   = this.config.hireline?.maxPages ?? 5;
          this.configForm.hlEnabled    = this.config.hireline?.enabled  ?? true;
          this.configForm.pauseMs          = this.config.pauseMs          ?? 20000;
          this.configForm.autoMinPauseMs   = this.config.autoMinPauseMs   ?? 20000;
          this.configForm.autoMaxPauseMs   = this.config.autoMaxPauseMs   ?? 50000;
          const cronRaw = this.config.cron ?? '0 0 3 * * MON-SAT';
          const cronParts = cronRaw.trim().split(/\s+/);
          const horaFromCron = cronParts.length >= 3 ? parseInt(cronParts[2], 10) : 3;
          this.configForm.horaEjecucion = isNaN(horaFromCron) ? 3 : horaFromCron;
          this.configForm.cron = `0 0 ${this.configForm.horaEjecucion} * * MON-SAT`;
          const cron2Raw = this.config.cron2 ?? '';
          const cron2Parts = cron2Raw.trim().split(/\s+/);
          const hora2 = cron2Parts.length >= 3 ? parseInt(cron2Parts[2], 10) : NaN;
          this.configForm.horaEjecucion2 = (!cron2Raw || isNaN(hora2)) ? -1 : hora2;
          const cron3Raw = this.config.cron3 ?? '';
          const cron3Parts = cron3Raw.trim().split(/\s+/);
          const hora3 = cron3Parts.length >= 3 ? parseInt(cron3Parts[2], 10) : NaN;
          this.configForm.horaEjecucion3 = (!cron3Raw || isNaN(hora3)) ? -1 : hora3;
          this.configForm.iaModelo         = this.config.iaModelo          ?? 'deepseek-v4-flash';
          // iaToken no se pre-llena — se gestiona en /admin/config
          this.iaSet = this.config.iaSet === true;
          this.configForm.apifyActors = (this.config.apifyActors ?? []).map((a: any) => ({
            id: a.id,
            label: a.label ?? 'LinkedIn',
            sourceType: a.sourceType ?? 'LINKEDIN',
            enabled: a.enabled ?? false,
            actorId: a.actorId ?? 'curious_coder/linkedin-jobs-scraper',
            token: '', // nunca se pre-llena — se revela por actor bajo demanda
            queries: (a.queries ?? []).join('\n'),
            locations: (a.locations ?? []).join('\n'),
            workplaceTypes: a.workplaceTypes ?? 'remote',
            datePosted: a.datePosted ?? 'past_24_hours',
            soloSolicitudSencilla: a.soloSolicitudSencilla ?? false,
            maxItemsPerRun: a.maxItemsPerRun ?? a.limitPerQuery ?? 25,
            queriesPerRun: a.queriesPerRun ?? 8,
            categories: (a.categories ?? []).join('\n'),
            maxItems: a.maxItems ?? 300,
            run: a.run ?? 'BOTH',
            tokenMasked: a.tokenMasked ?? '',
            tokenSet: a.tokenSet ?? false
          }));
          this.configForm.remotiveEnabled      = this.config.remotive?.enabled      ?? true;
          this.configForm.remotiveMaxJobs      = this.config.remotive?.maxJobs      ?? 100;
          this.configForm.remotiveRun          = this.config.remotive?.run          ?? 'BOTH';
          this.configForm.himalayanEnabled     = this.config.himalayan?.enabled     ?? true;
          this.configForm.himalayanMaxJobs     = this.config.himalayan?.maxJobs     ?? 100;
          this.configForm.himalayanRun         = this.config.himalayan?.run         ?? 'BOTH';
          this.configForm.workingNomadsEnabled = this.config.workingNomads?.enabled ?? true;
          this.configForm.workingNomadsMaxJobs = this.config.workingNomads?.maxJobs ?? 100;
          this.configForm.workingNomadsRun     = this.config.workingNomads?.run     ?? 'BOTH';
          this.configForm.wwrEnabled           = this.config.wwr?.enabled           ?? true;
          this.configForm.wwrMaxJobs           = this.config.wwr?.maxJobs           ?? 100;
          this.configForm.wwrRun               = this.config.wwr?.run               ?? 'BOTH';
          this.configForm.occRun               = this.config.occ?.run               ?? 'BOTH';
          this.configForm.ctRun                = this.config.computrabajo?.run      ?? 'BOTH';
          this.configForm.hlRun                = this.config.hireline?.run          ?? 'BOTH';
          this.configForm.premiumProxies       = this.config.premiumProxies          ?? '';
        }
      },
      error: () => { this.config = null; }
    });
  }

  cargarHistorial(): void {
    this.cargandoHistorial = true;
    this.scraperService.getHistorial().subscribe({
      next: (res) => {
        const runs: ScrapeRun[] = res?.data ?? res;
        this.historial = runs.map(r => ({
          ...r,
          metricas: this.parseFuenteMetricas(r.fuentes),
          errores: this.parseErrorLog(r.errorLog),
          apifyActors: this.parseApifyActorsJson(r.apifyActorsJson)
        }));
        this.cargandoHistorial = false;
      },
      error: () => { this.cargandoHistorial = false; }
    });
  }

  /** Parsea el desglose por-actor de Apify: [{"id","label","count","dup","saved","error"}] */
  parseApifyActorsJson(json?: string): ApifyActorStat[] {
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }

  parseErrorLog(errorLog?: string): { occ?: string; ct?: string; hl?: string; apify?: string; remotive?: string; himalayan?: string; workingNomads?: string; wwr?: string } | null {
    if (!errorLog) return null;
    try {
      const parsed = JSON.parse(errorLog);
      const result: any = {};
      if (parsed.occ)          result.occ          = parsed.occ;
      if (parsed.ct)           result.ct           = parsed.ct;
      if (parsed.hl)           result.hl           = parsed.hl;
      if (parsed.apify)        result.apify        = parsed.apify;
      if (parsed.remotive)     result.remotive     = parsed.remotive;
      if (parsed.himalayan)    result.himalayan    = parsed.himalayan;
      if (parsed.workingNomads) result.workingNomads = parsed.workingNomads;
      if (parsed.wwr)          result.wwr          = parsed.wwr;
      return Object.keys(result).length > 0 ? result : null;
    } catch { return null; }
  }

  /** Parsea dupSampleUrls y genera HTML con título, empresa, url encontrada y url en nuestra bolsa */
  renderDupUrls(texto: string): string {
    if (!texto) return '';
    const BASE = 'https://tg-empleos.tgconsulting.online';
    let html = '';
    const lineas = texto.split('\n').filter(l => l.trim());
    for (const linea of lineas) {
      // Encabezado de fuente: "[HIRELINE] 20 dups: entry1 ||| entry2"
      const matchFuente = linea.match(/^\[([^\]]+)\]\s*(\d+)\s*dups?:\s*(.*)/s);
      if (!matchFuente) continue;
      const fuente = matchFuente[1];
      const total = matchFuente[2];
      const entriesRaw = matchFuente[3];
      html += `<div style="margin-bottom:10px;">`;
      html += `<div style="font-size:11px;font-weight:700;color:#1e40af;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;">${fuente} — ${total} omitidas (muestra)</div>`;
      // Entries separadas por "  |  "
      const entries = entriesRaw.split(/\s{2,}\|\s{2,}|\s*\|\|\|\s*/);
      for (const entry of entries) {
        if (!entry.trim()) continue;
        const parts = entry.trim().split(':::');
        if (parts.length >= 5) {
          // Nuevo formato: titulo:::empresa:::urlEncontrada:::urlBolsa:::criterio
          const [titulo, empresa, urlEncontrada, urlBolsa, criterio] = parts;
          html += `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:8px 10px;margin-bottom:6px;font-size:11px;">`;
          html += `<div style="font-weight:600;color:#0f172a;margin-bottom:4px;">"${titulo}"${empresa ? ' en <em>' + empresa + '</em>' : ''}</div>`;
          html += `<div style="color:#64748b;margin-bottom:2px;">Razón: <span style="color:#7c3aed;">${criterio}</span></div>`;
          if (urlEncontrada) {
            html += `<div style="margin-top:4px;"><span style="color:#64748b;font-size:10px;">Encontrada en fuente:</span> <a href="${urlEncontrada}" target="_blank" rel="noopener" style="color:#2563eb;word-break:break-all;">${urlEncontrada}</a></div>`;
          }
          if (urlBolsa) {
            const bolsaHref = `${BASE}/vacantes/detalles/${urlBolsa}`;
            html += `<div style="margin-top:2px;"><span style="color:#64748b;font-size:10px;">En nuestra bolsa:</span> <a href="${bolsaHref}" target="_blank" rel="noopener" style="color:#16a34a;font-weight:600;word-break:break-all;">${bolsaHref}</a></div>`;
          } else {
            html += `<div style="margin-top:2px;font-size:10px;color:#94a3b8;">Nuestra bolsa: no disponible (puede haberse eliminado)</div>`;
          }
          html += `</div>`;
        } else {
          // Formato legacy — mostrar como texto con links
          let e = entry.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          e = e.replace(/(https?:\/\/[^\s|]+)/g, '<a href="$1" target="_blank" style="color:#2563eb;">$1</a>');
          html += `<div style="padding:4px 8px;font-size:11px;color:#475569;">${e}</div>`;
        }
      }
      html += `</div>`;
    }
    return html;
  }

  parseFuenteMetricas(fuentes: string) {
    if (!fuentes || !fuentes.includes('|')) return null;
    const parts: Record<string, number> = {};
    fuentes.split('|').forEach(p => {
      const eq = p.indexOf('=');
      if (eq >= 0) parts[p.substring(0, eq).toLowerCase()] = parseInt(p.substring(eq + 1) || '0', 10);
    });
    return {
      occ: parts['occ'] ?? 0, ct: parts['ct'] ?? 0, hl: parts['hl'] ?? 0,
      apify: parts['ap'] ?? parts['apify'] ?? 0,
      remotive: parts['remotive'] ?? parts['rem'] ?? 0,
      himalayan: parts['himalayan'] ?? parts['him'] ?? 0,
      workingNomads: parts['workingnomads'] ?? parts['wn'] ?? 0,
      wwr: parts['wwr'] ?? 0,
      occDup: parts['occ_d'] ?? 0,
      ctDup: parts['ct_d'] ?? 0,
      hlDup: parts['hl_d'] ?? 0,
      apifyDup: parts['apify_d'] ?? 0,
      remotiveDup: parts['rem_d'] ?? 0,
      himalayanDup: parts['him_d'] ?? 0,
      workingNomadsDup: parts['wn_d'] ?? 0,
      wwrDup: parts['wwr_d'] ?? 0,
      detOk: parts['d+'] ?? 0, detFail: parts['d-'] ?? 0,
      llmOk: parts['l+'] ?? 0, llmFail: parts['l-'] ?? 0,
      dup: parts['dup'] ?? 0
    };
  }

  mostrarConfirmacionScraper = false;

  abrirConfirmacion(): void {
    if (this.ejecutando || this.scraperEnEjecucion) return;
    this.mostrarConfirmacionScraper = true;
  }

  cerrarConfirmacion(): void { this.mostrarConfirmacionScraper = false; }

  algunaFuenteActiva(): boolean {
    return this.configForm.occEnabled || this.configForm.ctEnabled || this.configForm.hlEnabled
      || this.apifyActorsActivos() > 0 || this.configForm.remotiveEnabled || this.configForm.himalayanEnabled
      || this.configForm.workingNomadsEnabled || this.configForm.wwrEnabled;
  }

  /** Cantidad de actors de Apify activos — usado en plantillas (Angular no permite arrow functions en bindings). */
  apifyActorsActivos(): number {
    return this.configForm.apifyActors.filter(a => a.enabled).length;
  }

  // Estado para el modal de huérfanos
  orphanCount = 0;
  mostrarConfirmacionOrphans = false;
  limpiandoOrphans = false;
  reparandoStatus = false;

  confirmarEjecutar(): void {
    this.mostrarConfirmacionScraper = false;
    this.ejecutando = true;
    this.mensajeEjecutar = 'Verificando registros huérfanos...';

    this.scraperService.countOrphans().subscribe({
      next: (res) => {
        this.orphanCount = res?.count ?? 0;
        if (this.orphanCount > 0) {
          this.ejecutando = false;
          this.mostrarConfirmacionOrphans = true;
        } else {
          this._iniciarScraper();
        }
      },
      error: () => {
        // Si falla la verificación, continuar igual
        this._iniciarScraper();
      }
    });
  }

  limpiarOrphansYEjecutar(): void {
    this.mostrarConfirmacionOrphans = false;
    this.limpiandoOrphans = true;
    this.mensajeEjecutar = `Eliminando ${this.orphanCount} registros huérfanos...`;

    this.scraperService.deleteOrphans().subscribe({
      next: () => {
        this.limpiandoOrphans = false;
        this.mensajeEjecutar = null;
        this._iniciarScraper();
      },
      error: () => {
        this.limpiandoOrphans = false;
        this._iniciarScraper();
      }
    });
  }

  omitirOrphansYEjecutar(): void {
    this.mostrarConfirmacionOrphans = false;
    this._iniciarScraper();
  }

  repararStatus(): void {
    if (this.reparandoStatus) return;
    this.reparandoStatus = true;
    this.mensajeEjecutar = 'Reparando status de vacantes...';
    this.scraperService.fixStatus().subscribe({
      next: (r: any) => {
        this.reparandoStatus = false;
        const diag = Object.entries(r || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(' | ');
        this.mensajeEjecutar = diag || 'Reparación completada.';
        setTimeout(() => {
          this.mensajeEjecutar = null;
          this.scraperService.stats().subscribe(s => { if (s?.data) this.stats = s.data; else this.stats = s; });
        }, 8000);
      },
      error: () => {
        this.reparandoStatus = false;
        this.mensajeEjecutar = 'Error al reparar status.';
      }
    });
  }

  private _iniciarScraper(): void {
    this.ejecutando = true;
    this.mensajeEjecutar = null;
    this.ultimoResumen = null;
    this.scraperService.ejecutarScraper().subscribe({
      next: (res) => {
        this.ejecutando = false;
        this.scraperEnEjecucion = true;
        this.progreso = { fuente: 'Iniciando...', count: 0, total: 0, guardadas: 0, occCount: 0, ctCount: 0, hlCount: 0, apifyCount: 0, apifySaved: 0, remotiveCount: 0, himalayanCount: 0, workingNomadsCount: 0, wwrCount: 0, dupRun: 0, dupBD: 0, occDup: 0, ctDup: 0, hlDup: 0, apifyDup: 0, remotiveDup: 0, himalayanDup: 0, workingNomadsDup: 0, wwrDup: 0, detOk: 0, detFail: 0, llmOk: 0, llmFail: 0, llmError: '', errGuardado: 0, occError: null, ctError: null, hlError: null, apifyError: null, remotiveError: null, himalayanError: null, workingNomadsError: null, wwrError: null, iniciadoPor: null, iniciadoEn: null, dupSampleUrls: '', apifyActors: [] };
        this.tabActiva = 'historial';
        this.iniciarPolling();
      },
      error: (err) => {
        this.mensajeEjecutar = err?.error?.message ?? 'Error al ejecutar el scraper.';
        this.ejecutando = false;
      }
    });
  }

  detenerScraper(): void {
    if (this.deteniendo) return;
    this.deteniendo = true;
    this.scraperService.detenerScraper().subscribe({
      next: (res: any) => {
        this.mensajeEjecutar = res?.message ?? 'Scraping detenido.';
        this.scraperEnEjecucion = false;
        this.deteniendo = false;
      },
      error: (err: any) => {
        this.mensajeEjecutar = err?.error?.message ?? 'Error al detener.';
        this.deteniendo = false;
      }
    });
  }

  private verificarEstadoScraper(): void {
    this.scraperService.getScraperStatus().subscribe({
      next: (res) => {
        const d = res?.data ?? (res as any);
        if (d?.running) {
          this.scraperEnEjecucion = true;
          this.progreso = this.mapProgreso(d);
          this.iniciarPolling();
        } else if (d?.guardadas > 0 && d?.iniciadoPor) {
          // Scraper no está corriendo pero hay datos de la última ejecución
          this.scraperEnEjecucion = false;
          this.progreso = this.mapProgreso(d);
          this.ultimoResumen = { ...this.progreso };
        }
      },
      error: () => {}
    });
  }

  private mapProgreso(d: any) {
    return {
      fuente: d.fuente ?? '',
      count: d.count ?? 0,
      total: d.total ?? 0,
      guardadas: d.guardadas ?? 0,
      occCount: d.occCount ?? 0,
      ctCount: d.ctCount ?? 0,
      hlCount: d.hlCount ?? 0,
      apifyCount: d.apifyCount ?? d.scraperApifyCount ?? 0,
      apifySaved: d.apifySaved ?? 0,
      remotiveCount: d.remotiveCount ?? 0,
      himalayanCount: d.himalayanCount ?? 0,
      workingNomadsCount: d.workingNomadsCount ?? 0,
      wwrCount: d.wwrCount ?? 0,
      dupRun: d.dupRun ?? 0,
      dupBD: d.dupBD ?? 0,
      occDup: d.occDup ?? 0,
      ctDup: d.ctDup ?? 0,
      hlDup: d.hlDup ?? 0,
      apifyDup: d.apifyDup ?? 0,
      remotiveDup: d.remotiveDup ?? 0,
      himalayanDup: d.himalayanDup ?? 0,
      workingNomadsDup: d.workingNomadsDup ?? 0,
      wwrDup: d.wwrDup ?? 0,
      detOk: d.detOk ?? 0,
      detFail: d.detFail ?? 0,
      llmOk: d.llmOk ?? 0,
      llmFail: d.llmFail ?? 0,
      llmError: d.llmError ?? '',
      errGuardado: d.errGuardado ?? 0,
      occError: d.occError ?? null,
      ctError: d.ctError ?? null,
      hlError: d.hlError ?? null,
      apifyError: d.apifyError ?? null,
      remotiveError: d.remotiveError ?? null,
      himalayanError: d.himalayanError ?? null,
      workingNomadsError: d.workingNomadsError ?? null,
      wwrError: d.wwrError ?? null,
      iniciadoPor: d.iniciadoPor ?? null,
      iniciadoEn: d.iniciadoEn ?? null,
      dupSampleUrls: d.dupSampleUrls ?? '',
      apifyActors: this.parseApifyActorsJson(d.apifyActorsJson)
    };
  }

  private iniciarPolling(): void {
    this.detenerPolling();
    this.pollingTimer = setInterval(() => {
      this.scraperService.getScraperStatus().subscribe({
        next: (res) => {
          const d = res?.data ?? (res as any);
          const running: boolean = d?.running ?? false;
          this.progreso = this.mapProgreso(d);
          if (!running && this.scraperEnEjecucion) {
            this.scraperEnEjecucion = false;
            this.detenerPolling();
            this.ultimoResumen = { ...this.progreso };
            this.cargarStats();
            this.cargarHistorial();
            const guardadas = this.progreso.guardadas;
            const bruto = this.progreso.count;
            const duplicados = this.progreso.dupRun + this.progreso.dupBD;
            this.mensajeEjecutar = `Scraping completado — ${guardadas} guardadas de ${bruto} encontradas (${duplicados} duplicadas).`;
          }
        },
        error: () => {}
      });
    }, 4000);
  }

  private detenerPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  probandoProxy = false;
  resultadoProxy: any = null;

  probarProxy(): void {
    const text = this.configForm.premiumProxies || '';
    const firstLine = text.split('\n')[0]?.trim();
    if (!firstLine) { this.resultadoProxy = { ok: false, error: 'No hay proxies' }; return; }
    this.probandoProxy = true; this.resultadoProxy = null;
    this.scraperService.proxyTest(firstLine).subscribe({
      next: (r: any) => { this.resultadoProxy = r; this.probandoProxy = false; },
      error: (e: any) => { this.resultadoProxy = { ok: false, error: e?.error?.error || e?.message }; this.probandoProxy = false; }
    });
  }

  guardarConfig(): void {
    if (this.guardandoConfig) return;
    this.guardandoConfig = true;
    this.mensajeConfig = null;
    const payload = {
      ...this.configForm,
      cron:  `0 0 ${this.configForm.horaEjecucion} * * MON-SAT`,
      cron2: this.configForm.horaEjecucion2 >= 0
             ? `0 0 ${this.configForm.horaEjecucion2} * * MON-SAT`
             : '',
      cron3: this.configForm.horaEjecucion3 >= 0
             ? `0 0 ${this.configForm.horaEjecucion3} * * MON-SAT`
             : '',
      apifyActors: this.configForm.apifyActors.map(a => ({
        ...a,
        queries: a.queries.split('\n').map((s: string) => s.trim()).filter((s: string) => s.length > 0),
        locations: a.locations.split('\n').map((s: string) => s.trim()).filter((s: string) => s.length > 0),
        categories: a.categories.split('\n').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
      }))
    };
    this.scraperService.updateConfig(payload).subscribe({
      next: (res) => {
        this.mensajeConfig = res?.message ?? 'Configuración guardada.';
        this.mensajeConfigError = false;
        this.guardandoConfig = false;
        this.cargarConfig();
      },
      error: () => {
        this.mensajeConfig = 'Error al guardar la configuración.';
        this.mensajeConfigError = true;
        this.guardandoConfig = false;
      }
    });
  }

  toggleRun(run: ScrapeRun): void {
    if (this.runExpandido === run.id) {
      this.runExpandido = null;
      return;
    }
    this.runExpandido = run.id;
    if (this.vacantesPorRun[run.id] && this.resultadosPorRun[run.id]) return;
    this.cargandoVacantes = run.id;
    this.scraperService.getRunVacantes(run.id).subscribe({
      next: (res) => {
        this.vacantesPorRun[run.id] = res?.data ?? res;
        this.scraperService.getRunResultados(run.id).subscribe({
          next: (trace) => {
            this.resultadosPorRun[run.id] = trace?.data ?? trace;
            this.cargandoVacantes = null;
          },
          error: () => { this.resultadosPorRun[run.id] = []; this.cargandoVacantes = null; }
        });
      },
      error: () => { this.cargandoVacantes = null; }
    });
  }

  fuenteLabel(f: string): string {
    return ({
      OCC: 'OCC Mundial', COMPUTRABAJO: 'Computrabajo', HIRELINE: 'Hireline', APIFY_LINKEDIN: 'LinkedIn (Apify)',
      REMOTIVE: 'Remotive', HIMALAYAS: 'Himalayas', WORKING_NOMADS: 'Working Nomads', WWR: 'We Work Remotely'
    } as any)[f] ?? f;
  }

  get usuarioLogueado(): string {
    const u = this.authService.currentUserValue;
    return u?.user?.nombre || u?.user?.correo || (u as any)?.correo || (u as any)?.nombre || '';
  }

  formatDuracion(segundos?: number | null): string {
    if (!segundos) return '';
    const m = Math.floor(segundos / 60);
    const s = segundos % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  cerrarSesion(): void {
    this.authService.cerrarSesion();
    // Forzar recarga completa para limpiar cualquier estado en memoria
    window.location.href = '/acceder';
  }

  cargarApifyBalance(actorId: string): void {
    this.cargandoBalance[actorId] = true;
    this.scraperService.getApifyBalance(actorId).subscribe({
      next: (res) => { this.apifyBalances[actorId] = res?.data ?? res; this.cargandoBalance[actorId] = false; },
      error: () => { this.cargandoBalance[actorId] = false; }
    });
  }

  agregarApifyActor(sourceType: 'LINKEDIN' | 'GETONBRD'): void {
    const base = {
      id: 'actor-' + Date.now(),
      label: sourceType === 'GETONBRD' ? 'Nuevo — Get on Board' : 'Nuevo — LinkedIn',
      sourceType,
      enabled: false,
      actorId: sourceType === 'GETONBRD' ? 'jungle_synthesizer/getonbrd-latam-tech-jobs-scraper' : 'curious_coder/linkedin-jobs-scraper',
      token: '',
      queries: '',
      locations: 'Mexico',
      workplaceTypes: 'remote',
      datePosted: 'past_24_hours',
      soloSolicitudSencilla: false,
      maxItemsPerRun: 25,
      queriesPerRun: 8,
      categories: 'programming\nsysadmin-devops-qa\ndata-science-analytics\nmobile-developer\nmachine-learning-ai',
      maxItems: 300,
      run: 'BOTH',
      tokenMasked: '',
      tokenSet: false
    };
    this.configForm.apifyActors.push(base);
  }

  eliminarApifyActor(index: number): void {
    const actor = this.configForm.apifyActors[index];
    if (!confirm(`¿Eliminar el actor "${actor.label}"? Se dejará de ejecutar en futuras corridas.`)) return;
    this.configForm.apifyActors.splice(index, 1);
  }

  vacantesDeRun(runId: number): any[] {
    const all = this.vacantesPorRun[runId] ?? [];
    if (this.filtroFuenteRun === 'ALL') return all;
    return all.filter(v => v.scrapeFuente === this.filtroFuenteRun);
  }

  noTiDeRun(runId: number): any[] {
    const all = this.resultadosPorRun[runId] ?? [];
    return all.filter(v => v.resultado === 'DESCARTADA_NO_TI'
      && (this.filtroFuenteRun === 'ALL' || v.fuente === this.filtroFuenteRun));
  }

  fallosDeRun(runId: number): any[] {
    const all = this.resultadosPorRun[runId] ?? [];
    return all.filter(v => v.resultado === 'LLM_FALLIDA' || v.resultado === 'ERROR_GUARDADO');
  }

  eliminarRun(run: ScrapeRun): void {
    const fecha = new Date(run.ejecutadoEn).toLocaleString('es-MX');
    if (!confirm(`¿Estás seguro de eliminar ${run.totalVacantes} vacante(s) guardadas en la bolsa de trabajo?\n\nEjecución del ${fecha}\nEsta acción también eliminará las postulaciones asociadas y no se puede deshacer.`)) return;
    this.eliminandoRun = run.id;
    this.scraperService.eliminarRun(run.id).subscribe({
      next: () => {
        this.historial = this.historial.filter(r => r.id !== run.id);
        delete this.vacantesPorRun[run.id];
        delete this.resultadosPorRun[run.id];
        if (this.runExpandido === run.id) this.runExpandido = null;
        this.eliminandoRun = null;
        this.cargarStats();
      },
      error: () => { this.eliminandoRun = null; }
    });
  }

  rechazarVacanteIndividual(runId: number, vacanteId: number): void {
    if (!confirm('¿Eliminar esta vacante de /vacantes? No volverá a ser scrapeada en futuros runs.')) return;
    this.rechazandoVacante.add(vacanteId);
    this.scraperService.rechazarVacanteIndividual(vacanteId).subscribe({
      next: () => {
        const lista = this.vacantesPorRun[runId];
        if (lista) {
          const v = lista.find((x: any) => x.id === vacanteId);
          if (v) v.activa = false;
        }
        this.rechazandoVacante.delete(vacanteId);
      },
      error: () => this.rechazandoVacante.delete(vacanteId)
    });
  }

  private allApifyTokensLoaded = false;
  private loadTokensFromBackend(onLoaded: () => void): void {
    if (this.allApifyTokensLoaded) { onLoaded(); return; }
    this.loadingTokens = true;
    this.scraperService.getTokens().subscribe({
      next: (res) => {
        const d = res?.data ?? res;
        this.revealedApifyTokens = d.apifyActorTokens ?? {};
        this.allApifyTokensLoaded = true;
        this.loadingTokens = false;
        onLoaded();
      },
      error: () => { this.loadingTokens = false; }
    });
  }

  toggleRevealApifyToken(actorId: string): void {
    const actor = this.configForm.apifyActors.find(a => a.id === actorId);
    if (!actor) return;
    if (this.mostrarApifyToken[actorId]) {
      this.mostrarApifyToken[actorId] = false;
      actor.token = '';
      return;
    }
    this.loadTokensFromBackend(() => {
      actor.token = this.revealedApifyTokens[actorId] ?? '';
      this.mostrarApifyToken[actorId] = true;
    });
  }
}
